import { type LoginParams, loginSchema } from '@argus-labs/auth-shared/auth'
import { VerifyType } from '@argus-labs/auth-shared/verify'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  SocialSignIn,
  socialProvidersQueryOptions,
  socialSignInSearch,
  useSocialSignInError,
} from '@/components/social-sign-in'
import { Button } from '@/components/ui/button'
import { ClearableInput } from '@/components/ui/clearable-input'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { PageCard } from '@/components/ui/page-card'
import { PasswordInput } from '@/components/ui/password-input'
import { Text } from '@/components/ui/text'
import { authClient } from '@/lib/auth-client'
import { authFlow, flowDestination, flowHeaders, upgradeFailureReason } from '@/lib/auth-flow'
import { otpVerification } from '@/lib/otp-verification'
import { invalidateSessionQuery } from '@/lib/session'
import { toast } from '@/lib/toast'
import { commonSearchSchema, generateTitle } from '@/lib/utils'

const loginPageSchema = commonSearchSchema.extend({
  // Keep email prefill links working without putting new credentials in the URL.
  email: z.email().optional().catch(undefined),
  ...socialSignInSearch,
})

export const Route = createFileRoute('/_signedOut/login')({
  validateSearch: loginPageSchema,
  loader: ({ context }) => context.queryClient.ensureQueryData(socialProvidersQueryOptions()),
  component: Login,
  head: () => ({ meta: [{ title: generateTitle('Sign In') }] }),
})

function Login() {
  const search = Route.useSearch()
  const { gameId } = search
  const isUpgrade = authFlow(search).kind === 'guestUpgrade'

  return (
    <PageCard
      title={isUpgrade ? 'Link Your Guest Progress' : 'Sign In'}
      description={
        isUpgrade ? (
          <>
            Sign in to link your guest progress in <b>{gameId}</b> to your Argus account.
          </>
        ) : undefined
      }
      footer={
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <Text size={12}>Don’t have an account?</Text>

          <Link to="/register" className="flex">
            <Text size={12} font="nbArchitekt" color="gold">
              Sign Up
            </Text>
          </Link>
        </div>
      }
    >
      <LoginForm />
    </PageCard>
  )
}

function LoginForm() {
  const search = Route.useSearch()
  const flow = authFlow(search)
  // Validate on submit, not blur: an error appearing on blur shifts the layout under a
  // click in progress, so a tap on a link or button below the field can miss.
  const form = useForm<LoginParams>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: search.email ?? '', password: '' },
  })
  const navigate = useNavigate()
  const [isSendingReset, setIsSendingReset] = useState(false)
  const isBusy = form.formState.isSubmitting || isSendingReset
  useSocialSignInError(search.error, handleForgotPassword)

  async function handleForgotPassword() {
    const email = form.getValues('email')
    form.setValue('email', email, { shouldTouch: true })
    if (!(await form.trigger('email', { shouldFocus: true }))) return

    try {
      setIsSendingReset(true)
      const { data, error } = await otpVerification.requestPasswordReset(email)
      if (data) {
        return navigate({ to: '/verify', search: { email, type: VerifyType.PasswordReset } })
      }
      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    } finally {
      setIsSendingReset(false)
    }
  }

  const onSubmit = async (values: LoginParams) => {
    try {
      const { data, error } = await authClient.signIn.email({
        ...values,
        fetchOptions: {
          headers: flowHeaders(flow),
        },
      })

      if (data) {
        invalidateSessionQuery()

        return navigate(flowDestination(flow))
      }

      if (error.code === 'EMAIL_NOT_VERIFIED') {
        otpVerification.startResendCooldown()
        return navigate({
          to: '/verify',
          search: { email: values.email, type: VerifyType.ConfirmEmail },
        })
      }

      // Inline under the password, where the person fixes it, instead of a toast at the top.
      if (error.code === 'INVALID_EMAIL_OR_PASSWORD') {
        return form.setError(
          'password',
          { message: 'Incorrect email or password.' },
          { shouldFocus: true },
        )
      }

      const reason = upgradeFailureReason(flow, error.code)
      if (reason) return navigate({ to: '/upgrade-failed', search: { reason } })

      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
      <FieldGroup>
        <SocialSignIn flow={flow} disabled={isBusy} />

        <Controller
          control={form.control}
          name="email"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid} data-disabled={field.disabled}>
              <FieldLabel htmlFor="login-email">Email Address*</FieldLabel>
              <ClearableInput
                {...field}
                id="login-email"
                type="email"
                onClear={() => field.onChange('')}
                required
                autoFocus
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint="next"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                    event.preventDefault()
                    form.setFocus('password')
                  }
                }}
                placeholder="you@example.com"
                readOnly={isBusy}
                aria-invalid={fieldState.invalid}
                aria-describedby={fieldState.invalid ? 'login-email-error' : undefined}
              />
              <FieldError id="login-email-error" errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="password"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid} data-disabled={field.disabled}>
              <div className="flex items-center">
                <FieldLabel htmlFor="login-password">Password*</FieldLabel>
                {/* The pseudo-element keeps a 44px target without pushing the input down. */}
                <Button
                  variant="link"
                  size="sm"
                  className="relative ml-auto min-h-0 py-0 leading-4 after:absolute after:inset-x-0 after:-top-5 after:-bottom-2"
                  disabled={isBusy}
                  onClick={handleForgotPassword}
                >
                  {isSendingReset ? 'Sending code…' : 'Forgot password?'}
                </Button>
              </div>
              <PasswordInput
                {...field}
                id="login-password"
                required
                autoComplete="current-password"
                enterKeyHint="go"
                readOnly={isBusy}
                aria-invalid={fieldState.invalid}
                aria-describedby={fieldState.invalid ? 'login-password-error' : undefined}
              />
              <FieldError id="login-password-error" errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Button type="submit" disabled={isBusy} className="w-full" aria-busy={isBusy}>
          {form.formState.isSubmitting ? 'Signing in…' : 'Sign In'}
        </Button>
      </FieldGroup>
    </form>
  )
}
